import React from 'react';

import { Alert, Typography } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import styled from 'styled-components';

import { SYNMATCH_COLORS, synmatchTheme } from 'theme';

// An alert action is an arbitrary node, so its variant cannot be set as a prop
// from here. Scoping the default to this subtree means an alert cannot be built
// with an actionless-looking action, while a caller passing its own `variant`
// still wins.
const alertActionTheme = createTheme(synmatchTheme, {
  components: {
    MuiButton: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        // MUI outlines at 0.5 alpha, which lands on ~3.05:1 against the status
        // tints: passing, but inside rounding of the 3:1 minimum. The full
        // Oxford value is unambiguous on all four.
        outlined: { borderColor: SYNMATCH_COLORS.primary },
      },
    },
  },
});

const AlertAction: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ThemeProvider theme={alertActionTheme}>{children}</ThemeProvider>
);

// Colour, border, and radius come from the shared MuiAlert theme override, so
// this carries only what density actually changes.
const CompactAlert = styled(Alert)`
  && {
    align-items: center;
    padding: 0.625rem 0.875rem;
  }

  && .MuiAlert-icon {
    margin-right: 0.625rem;
    padding: 0;
  }

  && .MuiAlert-message {
    align-items: center;
    display: flex;
    flex: 1;
    min-width: 0;
    padding: 0;
  }

  && .MuiAlert-action {
    align-items: center;
    margin-left: 0.75rem;
    margin-right: 0;
    padding: 0;
  }
`;

const CompactAlertContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
`;

const CompactAlertHeading = styled(Typography).attrs({
  component: 'p',
  variant: 'body2',
})`
  && {
    font-weight: 500;
  }
`;

const CompactAlertMessage = styled(Typography).attrs({
  variant: 'body2',
})`
  && {
    color: ${SYNMATCH_COLORS.text.muted};
  }
`;

type StateAlertDensity = 'standard' | 'compact';

interface StateAlertProps {
  action?: React.ReactNode;
  // Defaults to MUI Alert's assertive `role="alert"`. Pass `role="status"` with
  // `aria-live="polite"` for non-urgent notices so screen readers announce them
  // without interrupting.
  'aria-live'?: 'assertive' | 'polite';
  density?: StateAlertDensity;
  focusOnMount?: boolean;
  heading: string;
  headingComponent?: React.ElementType;
  headingVariant?: 'h5' | 'h6';
  message?: string;
  role?: 'alert' | 'status';
  severity: 'error' | 'info' | 'warning';
}

export const StateAlert: React.FC<StateAlertProps> = ({
  action,
  'aria-live': ariaLive,
  density = 'standard',
  focusOnMount = false,
  heading,
  headingComponent = 'h2',
  headingVariant = 'h6',
  message,
  role,
  severity,
}) => {
  const alertRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (focusOnMount) alertRef.current?.focus();
  }, [focusOnMount]);

  if (density === 'compact') {
    return (
      <CompactAlert
        ref={alertRef}
        tabIndex={focusOnMount ? -1 : undefined}
        action={action ? <AlertAction>{action}</AlertAction> : undefined}
        aria-live={ariaLive}
        role={role}
        severity={severity}
      >
        <CompactAlertContent>
          <CompactAlertHeading>{heading}</CompactAlertHeading>

          {message ? (
            <CompactAlertMessage>{message}</CompactAlertMessage>
          ) : null}
        </CompactAlertContent>
      </CompactAlert>
    );
  }

  return (
    <Alert
      ref={alertRef}
      tabIndex={focusOnMount ? -1 : undefined}
      aria-live={ariaLive}
      role={role}
      severity={severity}
    >
      <Typography component={headingComponent} variant={headingVariant}>
        {heading}
      </Typography>

      {message ? <Typography>{message}</Typography> : null}

      {action ? <AlertAction>{action}</AlertAction> : null}
    </Alert>
  );
};
